import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readNotizen } from "@/office/excel/analyse";
import { mitErsatz, pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_FREIGABE } from "../makros";

export const lektionFreigeben: Lesson = {
  id: "freigeben",
  title: "Notizen und freigegebene Bereiche",
  summary: "Stille Hinweise an Zellen – und ein Blattschutz, der manchen Personen mehr erlaubt.",
  durationMinutes: 10,
  learningGoals: [
    "Notizen von Kommentaren unterscheiden und einsetzen",
    "Mit „Bearbeitung von Bereichen zulassen“ einzelne Bereiche trotz Blattschutz freigeben",
  ],
  steps: [
    {
      id: "notizen",
      title: "Notizen",
      explanation: [
        "Neben den Kommentaren – Unterhaltungen mit Antworten und Erledigt-Status (Modul " +
          "„Namen“) – kennt Excel Notizen: gelbe Zettel ohne Antworten, früher selbst " +
          "„Kommentare“ genannt. Rechtsklick → „Neue Notiz“ oder Umschalt + F2. Eine rote " +
          "Ecke zeigt, dass eine Zelle eine Notiz hat.",
        "Notizen passen für bleibende Erläuterungen: „Wert laut Jahresabschluss“, " +
          "„Formel nicht ändern – wird von Blatt Bericht gelesen“. Kommentare passen für " +
          "Fragen, die beantwortet und dann erledigt werden.",
      ],
      keyPoints: [
        "Umschalt + F2 = neue Notiz.",
        "Notiz = bleibende Erläuterung, Kommentar = Unterhaltung.",
      ],
      task: {
        instruction: "Füge einer Zelle mit einer wichtigen Formel eine Notiz hinzu, die erklärt, was sie berechnet.",
        hint: "Zelle markieren, Umschalt + F2, Text schreiben, Klick daneben.",
        macro: MAKRO_FREIGABE,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            async () => {
              const notizen = await readNotizen();
              return notizen ? notizen.filter((notiz) => notiz.content.length > 0).length : null;
            },
            MAKRO_FREIGABE,
            (bericht) => bericht.zahl("notizen") ?? 0,
            (anzahl) =>
              fromCriteria([criterion(`Notizen in der Arbeitsmappe: ${anzahl}`, anzahl > 0)], {
                success: "Die Erläuterung steht dort, wo sie gebraucht wird.",
                failure: "Es gibt noch keine Notiz.",
                hint: "„Neuer Kommentar“ legt eine Unterhaltung an, keine Notiz – hier ist „Neue Notiz“ gefragt.",
              }),
          ),
      },
    },
    {
      id: "bereiche-zulassen",
      title: "Bearbeitung von Bereichen zulassen",
      explanation: [
        "Der Blattschutz aus dem Modul „Namen“ sperrt alle Zellen außer den entsperrten. " +
          "„Überprüfen“ → „Bearbeitung von Bereichen zulassen“ geht einen Schritt weiter: " +
          "Einzelne Bereiche bekommen einen Titel und auf Wunsch ein eigenes Kennwort. Wer das " +
          "Kennwort kennt, darf dort trotz Blattschutz ändern, alle anderen nicht.",
        "Typischer Einsatz: Eine Planungsmappe, in der jede Abteilung nur ihre eigene " +
          "Spalte ausfüllen darf. Wichtig: Die Bereiche wirken erst, wenn das Blatt danach " +
          "geschützt wird – im Dialog gibt es dafür die Schaltfläche „Blatt schützen“.",
      ],
      keyPoints: [
        "Überprüfen → Bearbeitung von Bereichen zulassen → Neu.",
        "Titel, Bereich und optional ein Kennwort.",
        "Wirkt erst mit Blattschutz.",
      ],
      task: {
        instruction:
          "Lege einen freigegebenen Bereich mit Titel an und schütze danach das Blatt.",
        hint: "Überprüfen → Bearbeitung von Bereichen zulassen → Neu → Titel „Eingaben“, Zellen wählen → OK → Blatt schützen.",
        macro: MAKRO_FREIGABE,
        check: () =>
          pruefeMitMakro(MAKRO_FREIGABE, (bericht) => {
            const bereiche = [...bericht.gruppe("bereich").values()];
            return fromCriteria(
              [
                criterion(
                  bereiche[0]
                    ? `Freigegebener Bereich „${bereiche[0].get("titel")}“: ${bereiche[0].get("adresse")}`
                    : "Ein freigegebener Bereich ist angelegt",
                  bereiche.length > 0,
                ),
                criterion("Mindestens ein Blatt ist geschützt", (bericht.zahl("geschuetzteBlaetter") ?? 0) > 0),
              ],
              {
                success: "Das Blatt ist geschützt – bis auf den Bereich, der ausdrücklich freigegeben ist.",
                failure: "Freigabe oder Blattschutz fehlen noch.",
              },
            );
          }),
      },
    },
  ],
};
